import * as React from 'react';
import {
  Heading as ChakraHeading,
  type HeadingProps as ChakraHeadingProps,
} from '@chakra-ui/react';

/**
 * Project Heading.
 *
 * All headings should be imported from here, not from `@chakra-ui/react`
 * directly (enforced via eslint `no-restricted-imports`).
 *
 * NB-parity defaults matching the old NativeBase `Heading` baseStyle:
 * `color="secondary.500"`, `textAlign="center"`, `fontWeight="normal"`
 * (400), `lineHeight="shorter"`. Renders the brand `Recoleta` font via
 * the `heading` font token.
 *
 * **NB→Chakra size remap**: NB and Chakra v3 disagree on what each size
 * token *visually* means (NB `md`=24px vs Chakra `md`=16px). Migrated
 * call sites pass NB-style sizes; this wrapper remaps them so existing
 * visuals are preserved.
 */
const nbToChakraSize: Record<string, string> = {
  xs: 'lg',
  sm: 'xl',
  md: '2xl',
  lg: '3xl',
  xl: '4xl',
  '2xl': '5xl',
};

export type HeadingProps = ChakraHeadingProps;

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      size,
      fontWeight = 'normal',
      lineHeight = 'shorter',
      color = 'secondary.500',
      textAlign = 'center',
      // Recoleta headings are drawn with negative tracking in every Figma
      // frame (H2 mobile is -2%).
      letterSpacing = '-0.02em',
      ...rest
    },
    ref
  ) => {
    const remappedSize =
      typeof size === 'string' && size in nbToChakraSize
        ? (nbToChakraSize[size] as ChakraHeadingProps['size'])
        : size;

    return (
      <ChakraHeading
        ref={ref}
        size={remappedSize}
        fontWeight={fontWeight}
        lineHeight={lineHeight}
        color={color}
        textAlign={textAlign}
        letterSpacing={letterSpacing}
        {...rest}
      />
    );
  }
);
Heading.displayName = 'Heading';
